import React, { memo, useState } from "react";
import { Text, View, TouchableOpacity } from "react-native";
import { Image } from "expo-image";
import Svg, { Path } from "react-native-svg";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { Shadows } from "@/constants/Shadows";
import { formatCurrency } from "@/utils/myMoney";
import { useBrandLogo } from "@/hooks/useBrandLogo";
import {
  UserFinanceDataResponse,
  Subscription,
  BillingCadence,
} from "@/types/financeData";
import dayjs from "dayjs";
import useWindow from "@/hooks/useWindow";

interface SpendingSubscriptionsCardProps {
  financeData: UserFinanceDataResponse | null;
}

// Calculate days until next charge based on billing cadence
const getDaysUntilNextCharge = (
  firstTransactionAt: string,
  cadence: BillingCadence,
): number => {
  const today = dayjs();
  let nextDate = dayjs(firstTransactionAt);

  // Get cadence interval in months/days
  const cadenceMonths: Record<string, number> = {
    weekly: 0,
    biweekly: 0,
    monthly: 1,
    quarterly: 3,
    semiannual: 6,
    annual: 12,
  };
  const cadenceDays: Record<string, number> = {
    weekly: 7,
    biweekly: 14,
  };

  // Move next date forward until it's in the future
  if (cadence === "weekly" || cadence === "biweekly") {
    const days = cadenceDays[cadence] || 30;
    while (!nextDate.isAfter(today, "day")) {
      nextDate = nextDate.add(days, "day");
    }
  } else {
    const months = cadenceMonths[cadence] || 1;
    while (!nextDate.isAfter(today, "day")) {
      nextDate = nextDate.add(months, "month");
    }
  }

  return nextDate.diff(today, "day");
};

// Get initial letter for fallback logo
const getInitialLetter = (name: string): string => {
  return name.charAt(0).toUpperCase();
};

// Dropdown icon
const DropdownIcon = ({ isOpen }: { isOpen: boolean }) => (
  <Svg
    width={16}
    height={16}
    viewBox="0 0 16 16"
    fill="none"
    style={{ transform: [{ rotate: isOpen ? "0deg" : "-90deg" }] }}
  >
    <Path
      d="M4 6L8 10L12 6"
      stroke="#0F9129"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// Subscription item component
const SubscriptionItem = ({
  subscription,
  isLast,
}: {
  subscription: Subscription;
  isLast: boolean;
}) => {
  const { icon } = useBrandLogo(subscription.merchantName);
  const daysUntil = getDaysUntilNextCharge(
    subscription.firstTransactionAt,
    subscription.billingCadence,
  );

  return (
    <View>
      <View style={styles.subscriptionRow}>
        {/* Logo */}
        <View style={styles.logoContainer}>
          {icon ? (
            <Image source={{ uri: icon }} style={styles.logo} />
          ) : (
            <View style={styles.logoFallback}>
              <Text style={styles.logoInitial}>
                {getInitialLetter(subscription.merchantName)}
              </Text>
            </View>
          )}
        </View>

        {/* Name and days */}
        <View style={styles.subscriptionInfo}>
          <Text style={styles.subscriptionName} numberOfLines={1}>
            {subscription.merchantName}
          </Text>
          <Text style={styles.daysLabel}>In {daysUntil} days</Text>
        </View>

        {/* Amount and chevron */}
        <Text style={styles.amount}>{formatCurrency(subscription.amount)}</Text>
      </View>

      {!isLast && <View style={styles.divider} />}
    </View>
  );
};

export default memo(function SpendingSubscriptionsCard({
  financeData,
}: SpendingSubscriptionsCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const subscriptions = financeData?.analysis?.spending?.subscriptions || [];
  const { isDesktop } = useWindow();

  // Show empty state if no subscriptions
  if (!subscriptions.length) {
    return (
      <View style={styles.card}>
        <TouchableOpacity
          style={styles.header}
          onPress={() => setIsExpanded(!isExpanded)}
        >
          <DropdownIcon isOpen={isExpanded} />
          <Text style={styles.headerTitle}>Subscriptions</Text>
        </TouchableOpacity>
        {isExpanded && (
          <View style={styles.emptyState}>
            <Text style={styles.emptyTitle}>No subscriptions found</Text>
            <Text style={styles.emptySubtitle}>
              We&apos;ll detect your recurring charges automatically
            </Text>
          </View>
        )}
      </View>
    );
  }

  return (
    <View style={[styles.card, { marginHorizontal: isDesktop ? 0 : 16 }]}>
      {/* Header */}
      <TouchableOpacity
        style={styles.header}
        onPress={() => setIsExpanded(!isExpanded)}
      >
        <DropdownIcon isOpen={isExpanded} />
        <Text style={styles.headerTitle}>Subscriptions</Text>
      </TouchableOpacity>

      {/* Subscriptions list */}
      {isExpanded && (
        <View style={styles.subscriptionsList}>
          {subscriptions.map((subscription, index) => (
            <SubscriptionItem
              key={subscription.merchantName + index}
              subscription={subscription}
              isLast={index === subscriptions.length - 1}
            />
          ))}
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 16,
    ...Shadows.card,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  headerTitle: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  subscriptionsList: {
    gap: 0,
  },
  subscriptionRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    gap: 12,
  },
  logoContainer: {
    width: 40,
    height: 40,
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  logoFallback: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#F3F4F6",
    alignItems: "center",
    justifyContent: "center",
  },
  logoInitial: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  subscriptionInfo: {
    flex: 1,
    gap: 2,
  },
  subscriptionName: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    lineHeight: 22,
  },
  daysLabel: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
    lineHeight: 18,
  },
  amount: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    lineHeight: 22,
  },
  divider: {
    height: 1,
    backgroundColor: "#F3F3F3",
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: 24,
    gap: 8,
  },
  emptyTitle: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  emptySubtitle: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
    textAlign: "center",
  },
});
